import { queryOptions } from "@tanstack/react-query";
import { UserService } from "../service/UserService";
import { FolderService } from "../service/FolderService";
import { Folder } from "../util/Types/Folder";
import { normalizeFolders, normalizeSpaces } from "../util/sidebarNormalizer";

const loadFolderDetails = async (
  folderRef: { id: string; parentId?: string | null },
): Promise<Folder | null> => {
  try {
    const folderData = await FolderService.getOne(folderRef.id);
    return {
      ...folderData.data,
      parentId: folderData.data.parentId ?? folderRef.parentId ?? null,
      folders: [],
    };
  } catch (error) {
    console.error("Failed to load folder:", error);
    return null;
  }
};

const buildFolderTree = (flatFolders: Folder[]): Folder[] => {
  const folderMap = new Map<string, Folder>();
  flatFolders.forEach((f) => {
    folderMap.set(f.id, { ...f, folders: [] });
  });

  const rootFolders: Folder[] = [];
  folderMap.forEach((folder) => {
    if (folder.parentId && folderMap.has(folder.parentId)) {
      folderMap.get(folder.parentId)!.folders.push(folder);
    } else {
      rootFolders.push(folder);
    }
  });

  return rootFolders;
};

export const sidebarQueries = {
  folders: () =>
    queryOptions({
      queryKey: ["sidebar", "folders"],
      queryFn: async () => {
        const { data } = await FolderService.getFolders();
        const folderRefs: Array<{ id: string; parentId?: string | null }> = data || [];

        const loadedFolders = await Promise.all(
          folderRefs.map((ref) => loadFolderDetails(ref)),
        );
        const validFolders = loadedFolders.filter((f): f is Folder => f !== null);

        const rootFolders = buildFolderTree(validFolders);

        return normalizeFolders(rootFolders);
      },
    }),

  spaces: () =>
    queryOptions({
      queryKey: ["sidebar", "spaces"],
      queryFn: async () => {
        const { data } = await UserService.getSpaces();
        const spacesWithFolders = await Promise.all(
          (data || []).map(async (space: any) => {
            const folderRefs: Array<{ id: string; parentId?: string | null }> =
              space.folder || [];

            const loadedFolders = await Promise.all(
              folderRefs.map((ref) => loadFolderDetails(ref)),
            );
            const validFolders = loadedFolders.filter(
              (f): f is Folder => f !== null,
            );

            const rootFolders = buildFolderTree(validFolders);

            return {
              ...space,
              folder: rootFolders,
            };
          }),
        );
        return normalizeSpaces(spacesWithFolders);
      },
    }),

  all: () =>
    queryOptions({
      queryKey: ["sidebar"],
      queryFn: async () => {
        const { data: foldersData } = await FolderService.getFolders();
        const folderRefs: Array<{ id: string; parentId?: string | null }> =
          foldersData || [];

        const loadedFolders = await Promise.all(
          folderRefs.map((ref) => loadFolderDetails(ref)),
        );
        const validFolders = loadedFolders.filter(
          (f): f is Folder => f !== null,
        );

        const rootFolders = buildFolderTree(validFolders);
        const normalizedFolders = normalizeFolders(rootFolders);

        const { data: spacesData } = await UserService.getSpaces();
        const spacesWithFolders = await Promise.all(
          (spacesData || []).map(async (space: any) => {
            const spaceFolderRefs: Array<{ id: string; parentId?: string | null }> =
              space.folder || [];

            const spaceLoadedFolders = await Promise.all(
              spaceFolderRefs.map((ref) => loadFolderDetails(ref)),
            );
            const spaceValidFolders = spaceLoadedFolders.filter(
              (f): f is Folder => f !== null,
            );

            const spaceRootFolders = buildFolderTree(spaceValidFolders);

            return {
              ...space,
              folder: spaceRootFolders,
            };
          }),
        );
        const normalizedSpaces = normalizeSpaces(spacesWithFolders);

        return {
          folders: normalizedFolders,
          spaces: normalizedSpaces,
        };
      },
    }),
};
